<script setup>
import { ref, computed } from "vue";
import {
  useGetArchivalApplications,
  useSubmitUnarchivalApplication,
  useCancelArchivalApplication,
} from "./view_vehicles_logic";
import LoadingArea from "@/components/utils/LoadingArea.vue";
import Pagination from "@/components/utils/Pagination.vue";
import Table from "@/components/utils/Table.vue";
import ActionButton from "@/components/utils/ActionButton.vue";
import Dialog from "@/components/utils/Dialog.vue";
import LabelledField from "@/components/utils/LabelledField.vue";
import { snackbarData } from "@/components/utils/snackbar/logic";
import { actions } from "@/utils/constants";

const currentPage = ref(1);
const archivalQuery = useGetArchivalApplications(currentPage);

const applications = computed(() => archivalQuery.data.value?.data ?? []);
const total = computed(() => archivalQuery.data.value?.total ?? 0);
const perPage = computed(() => archivalQuery.data.value?.per_page ?? 10);

// Table headers matching MY VEHICLES styling
const tableHeaders = [
  { text: "Application No.", value: "application_no" },
  { text: "Registration No", value: "registrationNumber" },
  { text: "Type", value: "type" },
  { text: "Submitted On", value: "created_at" },
  { text: "Reason", value: "remarks" },
  { text: "Status", value: "status", action: true },
  { text: "View", value: "view", action: true },
  // { text: "Action / Unarchive", value: "unarchive", action: true },
];

function statusColor(status) {
  switch ((status ?? "").toUpperCase()) {
    case "PENDING":
      return "orange";
    case "COMPLETED":
    case "APPROVED":
      return "success";
    case "DENIED":
    case "FAILED":
      return "error";
    case "CANCELLED":
      return "grey";
    default:
      return "grey";
  }
}

function statusLabel(status) {
  switch ((status ?? "").toUpperCase()) {
    case "PENDING":
      return "Pending Review";
    case "COMPLETED":
    case "APPROVED":
      return "Approved";
    case "DENIED":
      return "Denied";
    case "CANCELLED":
      return "Cancelled";
    default:
      return status ?? "—";
  }
}

function typeLabel(type) {
  switch ((type ?? "").toUpperCase()) {
    case "ARCHIVE":
      return "Archival Request";
    case "ACTIVATE":
      return "Unarchival Request";
    default:
      return type ?? "—";
  }
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ── View Application Details Dialog ───────────────────────────────────────────
const detailsDialogOpen = ref(false);
const selectedAppForView = ref(null);

function onViewApplicationClicked(app) {
  selectedAppForView.value = app;
  detailsDialogOpen.value = true;
}

// ── Unarchival Dialog & Cancellation ─────────────────────────────────────────
const unarchivalDialogOpen = ref(false);
const selectedAppForUnarchive = ref(null);
const unarchivalReason = ref("");

const submitUnarchivalMutation = useSubmitUnarchivalApplication();
const cancelApplicationMutation = useCancelArchivalApplication();

function onUnarchiveClicked(app) {
  selectedAppForUnarchive.value = app;
  unarchivalReason.value = "";
  unarchivalDialogOpen.value = true;
}

function onSubmitUnarchival() {
  if (!unarchivalReason.value || unarchivalReason.value.trim().length === 0) {
    snackbarData.text = "Please provide a reason for unarchiving";
    snackbarData.color = "warning";
    snackbarData.open = true;
    return;
  }

  const vehicleId = selectedAppForUnarchive.value?.vehicle_id;
  submitUnarchivalMutation.mutate(
    { vehicleId, remarks: unarchivalReason.value },
    {
      onSuccess() {
        unarchivalDialogOpen.value = false;
        selectedAppForUnarchive.value = null;
        archivalQuery.refetch();
        snackbarData.text =
          "Unarchival request submitted successfully! Pending officer review.";
        snackbarData.color = "success";
        snackbarData.open = true;
      },
      onError(error) {
        const msg =
          error?.response?.data?.message ||
          "Failed to submit unarchival application. Please try again.";
        snackbarData.text = msg;
        snackbarData.color = "error";
        snackbarData.open = true;
      },
    }
  );
}

function onCancelApplicationClicked(app) {
  if (!confirm("Are you sure you want to cancel this pending application?")) {
    return;
  }
  cancelApplicationMutation.mutate(
    { applicationId: app.id },
    {
      onSuccess() {
        archivalQuery.refetch();
        snackbarData.text = "Archival application cancelled successfully.";
        snackbarData.color = "success";
        snackbarData.open = true;
      },
      onError(error) {
        const msg =
          error?.response?.data?.message || "Failed to cancel application.";
        snackbarData.text = msg;
        snackbarData.color = "error";
        snackbarData.open = true;
      },
    }
  );
}
</script>

<template>
  <v-container fluid class="pa-0">
    <v-row>
      <v-col cols="12">
        <div class="text-h6 text-center font-weight-bold primary--text mb-4">
          My Archival & Unarchival Applications
        </div>
      </v-col>
    </v-row>

    <LoadingArea :loading="archivalQuery.isLoading.value" class="mt-2">
      <!-- Table matching MY VEHICLES formatting -->
      <v-row class="no-gutters">
        <v-col cols="12">
          <Table
            :headers="tableHeaders"
            :items="applications"
            :loading="archivalQuery.isLoading.value"
            emptyText="No archival applications found"
            headClasses="primary white--text"
            class="view-vehicles-table"
          >
            <template #item.application_no="{ item: app }">
              <span class="font-weight-bold primary--text">
                {{ app.application_no || "—" }}
              </span>
            </template>

            <template #item.registrationNumber="{ item: app }">
              <span class="font-weight-medium">
                {{ app.vehicle ? app.vehicle.registrationNumber : "—" }}
              </span>
            </template>

            <template #item.type="{ item: app }">
              <v-chip x-small outlined color="primary" class="font-weight-medium">
                {{ typeLabel(app.type) }}
              </v-chip>
            </template>

            <template #item.created_at="{ item: app }">
              {{ formatDate(app.created_at) }}
            </template>

            <template #item.remarks="{ item: app }">
              <span
                class="d-inline-block text-truncate"
                :style="{ maxWidth: '200px' }"
                :title="app.remarks"
              >
                {{ app.remarks || "—" }}
              </span>
            </template>

            <template #item.status="{ item: app }">
              <v-chip
                small
                :color="statusColor(app.status)"
                dark
                class="font-weight-medium"
              >
                {{ statusLabel(app.status) }}
              </v-chip>
            </template>

            <!-- Column 6: View Button -->
            <template #item.view="{ item: app }">
              <ActionButton
                :action="actions.view"
                inTable
                @click="onViewApplicationClicked(app)"
              />
            </template>

            <!-- Column 7: Unarchive / Action (Commented out)
            <template #item.unarchive="{ item: app }">
              <div :style="{ minWidth: '120px' }">
                <v-btn
                  v-if="(app.status ?? '').toUpperCase() === 'PENDING'"
                  x-small
                  color="error"
                  outlined
                  class="text-none"
                  :loading="cancelApplicationMutation.isLoading.value"
                  @click="onCancelApplicationClicked(app)"
                >
                  <v-icon left x-small>mdi-close-circle-outline</v-icon>
                  Cancel
                </v-btn>

                <v-btn
                  v-else-if="
                    (app.type ?? '').toUpperCase() === 'ARCHIVE' &&
                    ['COMPLETED', 'APPROVED'].includes((app.status ?? '').toUpperCase())
                  "
                  x-small
                  color="success"
                  depressed
                  class="text-none white--text"
                  @click="onUnarchiveClicked(app)"
                >
                  <v-icon left x-small>mdi-archive-arrow-up</v-icon>
                  Unarchive
                </v-btn>

                <span v-else class="text-caption grey--text">—</span>
              </div>
            </template>
            -->
          </Table>
        </v-col>
      </v-row>

      <!-- Pagination -->
      <v-row v-if="total > perPage" justify="end" class="mt-2">
        <v-col cols="auto">
          <Pagination
            :current-page="currentPage"
            :total-number-of-items="total"
            :items-per-page="perPage"
            @paginationClicked="currentPage = $event"
          />
        </v-col>
      </v-row>
    </LoadingArea>

    <!-- ── View Application Details Modal ────────────────────────────────────── -->
    <Dialog :dialogOpen.sync="detailsDialogOpen" max-width="600">
      <template #title>
        <v-icon left color="primary">mdi-file-document-outline</v-icon>
        Application Details
      </template>
      <template #text>
        <v-card flat v-if="selectedAppForView" class="pa-2">
          <v-list dense>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle>Application Number</v-list-item-subtitle>
                <v-list-item-title class="font-weight-bold text-subtitle-1 primary--text">
                  {{ selectedAppForView.application_no || "—" }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-divider />

            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle>Vehicle Registration</v-list-item-subtitle>
                <v-list-item-title class="font-weight-bold">
                  {{ selectedAppForView.vehicle ? selectedAppForView.vehicle.registrationNumber : "—" }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-content>
                <v-list-item-subtitle>Application Type</v-list-item-subtitle>
                <v-list-item-title>
                  {{ typeLabel(selectedAppForView.type) }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-divider />

            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle>Status</v-list-item-subtitle>
                <v-list-item-title>
                  <v-chip
                    small
                    :color="statusColor(selectedAppForView.status)"
                    dark
                    class="font-weight-medium mt-1"
                  >
                    {{ statusLabel(selectedAppForView.status) }}
                  </v-chip>
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-content>
                <v-list-item-subtitle>Date Submitted</v-list-item-subtitle>
                <v-list-item-title>
                  {{ formatDate(selectedAppForView.created_at) }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-divider />

            <v-list-item class="mt-2">
              <v-list-item-content>
                <v-list-item-subtitle class="font-weight-bold mb-1">
                  Client Reason / Remarks:
                </v-list-item-subtitle>
                <v-card outlined class="pa-3 background grey lighten-4">
                  {{ selectedAppForView.remarks || "No remarks provided." }}
                </v-card>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
      </template>
    </Dialog>

    <!-- ── Apply for Unarchival Dialog ──────────────────────────────────────── -->
    <Dialog :dialogOpen.sync="unarchivalDialogOpen" max-width="500">
      <template #title>
        <v-icon left color="success">mdi-archive-arrow-up</v-icon>
        Apply for Vehicle Unarchival
      </template>
      <template #text>
        <v-card flat class="pa-1">
          <div class="mb-3">
            Request unarchiving / reactivation for vehicle:
            <v-chip small color="primary" class="font-weight-bold ml-1">
              {{ selectedAppForUnarchive?.vehicle?.registrationNumber }}
            </v-chip>
          </div>
          <v-alert type="info" text dense icon="mdi-information">
            Your request will be submitted to County Revenue Officers for approval before the vehicle is reactivated.
          </v-alert>
          <LabelledField label="Reason for Unarchiving">
            <v-textarea
              v-model="unarchivalReason"
              outlined
              dense
              rows="3"
              placeholder="e.g. Vehicle back in commercial service, ownership resolved..."
            />
          </LabelledField>
          <div class="d-flex justify-end mt-3">
            <v-btn text @click="unarchivalDialogOpen = false">Cancel</v-btn>
            <v-btn
              color="success"
              class="ml-2 white--text"
              depressed
              :loading="submitUnarchivalMutation.isLoading.value"
              @click="onSubmitUnarchival"
            >
              Submit Unarchival Request
            </v-btn>
          </div>
        </v-card>
      </template>
    </Dialog>
  </v-container>
</template>

<style lang="scss" scoped>
:deep(.view-vehicles-table) {
  & td,
  & th {
    padding: 0 12px !important;
  }
}
</style>
